'use client';

import { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations, useLocale } from 'next-intl';
import { cn } from '@medizzconnect/shared';
import { useFilterStore } from '../filters/filter-store';
import { CategoryCard } from './category-card';
import { colorToGradient } from '@/lib/color-utils';
import type { Database } from '@medizzconnect/db';

type MedicalCategoryRow = Database['public']['Tables']['medical_categories']['Row'];

interface Subcategory {
  name: string;
  /** Deutscher Original-Key – für Filter-Store (DB-Spalte doctor_categories.subcategory_name) */
  name_original?: string;
  treatments: string[];
  /** Deutsche Original-Keys der Behandlungen – für Filter-Store */
  treatments_original?: string[];
}

interface CategoryScrollProps {
  categories: MedicalCategoryRow[];
  className?: string;
}

/**
 * V1-Style Kategorie-Scrollleiste: full-width, Gradient-Karten,
 * Portal-Dropdown für Subcategories/Treatments.
 */
export function CategoryScroll({ categories, className }: CategoryScrollProps) {
  const t = useTranslations('home');
  const locale = useLocale();
  const isRtl = locale === 'ar';
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    categoryId,
    setCategoryId,
    subcategoryName,
    setSubcategoryName,
    treatmentName,
    setTreatmentName,
  } = useFilterStore();

  const [showScrollLeft, setShowScrollLeft] = useState(false);
  const [showScrollRight, setShowScrollRight] = useState(true);

  /** Dropdown komplett schließen */
  const closeDropdown = useCallback(() => {
    setExpandedCategory(null);
    setExpandedSubcategory(null);
    setDropdownPosition(null);
  }, []);

  // Portal-Dropdown State (wie V1)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [expandedSubcategory, setExpandedSubcategory] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxWidth: number;
  } | null>(null);
  const categoryButtonRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const updateScrollIndicators = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollPos = Math.abs(el.scrollLeft);
    const maxScroll = el.scrollWidth - el.clientWidth - 1;
    if (isRtl) {
      // RTL: scrollLeft is negative in Chrome/Firefox
      setShowScrollRight(scrollPos > 0);
      setShowScrollLeft(scrollPos < maxScroll);
    } else {
      setShowScrollLeft(scrollPos > 0);
      setShowScrollRight(scrollPos < maxScroll);
    }
  }, [isRtl]);

  const scrollBy = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = direction === 'left' ? -200 : 200;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  }, []);

  // Dynamische Dropdown-Position bei Scroll/Resize aktualisieren
  useEffect(() => {
    if (!expandedCategory || !dropdownPosition) return;

    const updateDropdownPosition = () => {
      const buttonRef = categoryButtonRefs.current.get(expandedCategory);
      if (buttonRef) {
        const buttonRect = buttonRef.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        let left = buttonRect.left;
        const width = Math.min(buttonRect.width, viewportWidth - 20);

        if (left + width > viewportWidth - 10) left = viewportWidth - width - 10;
        if (left < 10) left = 10;

        setDropdownPosition({
          top: buttonRect.bottom,
          left,
          width,
          maxWidth: viewportWidth - 20,
        });
      }
    };

    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [expandedCategory, dropdownPosition]);

  // Dropdown-Click-Outside-Handler
  useEffect(() => {
    if (!expandedCategory || !dropdownPosition) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const dropdown = document.querySelector('[data-category-dropdown]');
      const categoryButtons = document.querySelectorAll('[data-category-button]');

      let isClickInsideCategory = false;
      categoryButtons.forEach((button) => {
        if (button.contains(target)) isClickInsideCategory = true;
      });

      if (!dropdown?.contains(target) && !isClickInsideCategory) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedCategory, dropdownPosition]);

  // Initialize scroll state
  useEffect(() => {
    updateScrollIndicators();
  }, [categories, updateScrollIndicators]);

  const handleCategoryClick = (category: MedicalCategoryRow, event: React.MouseEvent<HTMLDivElement>) => {
    const isCurrentlySelected = categoryId === category.id;

    // Filter setzen/zurücksetzen
    if (isCurrentlySelected) {
      setCategoryId(null);
    } else {
      setCategoryId(category.id);
    }

    // Subcategory-Dropdown öffnen/schließen
    const showSubcategoriesMenu = category.show_subcategories ?? true;
    const subs = parseSubs(category);
    const hasSubcategories = subs.length > 0;

    if (hasSubcategories && showSubcategoriesMenu && !isCurrentlySelected) {
      const isCurrentlyExpanded = expandedCategory === category.id;

      if (!isCurrentlyExpanded) {
        const buttonRect = event.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        let left = buttonRect.left;
        const width = Math.min(buttonRect.width, viewportWidth - 20);
        if (left + width > viewportWidth - 10) left = viewportWidth - width - 10;
        if (left < 10) left = 10;

        categoryButtonRefs.current.set(category.id, event.currentTarget as HTMLDivElement);
        setDropdownPosition({ top: buttonRect.bottom, left, width, maxWidth: viewportWidth - 20 });
        setExpandedCategory(category.id);
        setExpandedSubcategory(null);
      } else {
        closeDropdown();
      }
    } else {
      closeDropdown();
    }
  };

  const handleSubcategoryClick = (category: MedicalCategoryRow, subcategory: Subcategory) => {
    // Immer den deutschen Original-Key im Store speichern (Sprache unabhängig)
    const originalKey = subcategory.name_original ?? subcategory.name;
    const isSame = subcategoryName === originalKey;
    setSubcategoryName(isSame ? null : originalKey);
    setTreatmentName(null);

    const showTreatmentsMenu = category.show_treatments ?? true;
    const hasTreatments = subcategory.treatments?.length > 0;

    if (hasTreatments && showTreatmentsMenu && !isSame) {
      // Treatments vorhanden → Untermenu öffnen, Dropdown bleibt offen
      const subcategoryKey = `${category.id}-${originalKey}`;
      setExpandedSubcategory(
        expandedSubcategory === subcategoryKey ? null : subcategoryKey,
      );
    } else {
      // Keine Treatments → Dropdown automatisch schließen
      closeDropdown();
    }
  };

  const handleTreatmentClick = (originalTreatment: string) => {
    // Store enthält immer deutschen Original-Key
    setTreatmentName(treatmentName === originalTreatment ? null : originalTreatment);
    // Nach Treatment-Auswahl → Dropdown automatisch schließen
    closeDropdown();
  };

  return (
    <>
      <section className={cn('relative w-full bg-white border-b border-gray-100 overflow-hidden', className)}>
        {/* Left Arrow */}
        {showScrollLeft && (
          <button
            type="button"
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-gray-200 w-10 h-10 flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg"
            style={{ zIndex: 20 }}
            onClick={() => scrollBy('left')}
            aria-label="Nach links scrollen"
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Categories Container */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scrollbar-hide"
          onScroll={updateScrollIndicators}
          style={{ scrollBehavior: 'smooth' }}
        >
          {categories.map((category) => {
            const subs = parseSubs(category);
            const hasSubcategories = subs.length > 0;
            const showSubcategoriesMenu = category.show_subcategories ?? true;

            return (
              <CategoryCard
                key={category.id}
                category={category}
                isActive={categoryId === category.id}
                hasSubcategories={hasSubcategories && showSubcategoriesMenu}
                isExpanded={expandedCategory === category.id}
                onClick={(e) => handleCategoryClick(category, e)}
              />
            );
          })}
        </div>

        {/* Right Arrow */}
        {showScrollRight && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white border border-gray-200 w-10 h-10 flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg"
            style={{ zIndex: 20 }}
            onClick={() => scrollBy('right')}
            aria-label="Nach rechts scrollen"
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </section>

      {/* Portal-basiertes Dropdown */}
      {expandedCategory && dropdownPosition && typeof window !== 'undefined' &&
        createPortal(
          <div
            data-category-dropdown
            className="fixed border border-white/20 shadow-2xl rounded-lg max-h-80 overflow-y-auto animate-fade-in"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxWidth: dropdownPosition.maxWidth,
              zIndex: 999999,
              background: (() => {
                const category = categories.find((c) => c.id === expandedCategory);
                return category
                  ? colorToGradient(category.color || '#2563EB')
                  : colorToGradient('#138840');
              })(),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={closeDropdown}
              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-colors z-10"
              aria-label="Dropdown schließen"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {(() => {
              const category = categories.find((c) => c.id === expandedCategory);
              if (!category) return null;

              const subs = parseSubs(category);
              const showTreatmentsMenu = category.show_treatments ?? true;

              return subs.map((subcategory, index) => {
                const originalKey = subcategory.name_original ?? subcategory.name;
                const subcategoryKey = `${category.id}-${originalKey}`;
                const isSubcategoryExpanded = expandedSubcategory === subcategoryKey;
                const hasTreatments = subcategory.treatments?.length > 0;
                const shouldShowTreatments = hasTreatments && showTreatmentsMenu;

                return (
                  <div key={index} className="border-b border-white/20 last:border-b-0">
                    <button
                      type="button"
                      className={cn(
                        'w-full px-4 py-3 text-start hover:bg-white/10 transition-colors text-sm text-white',
                        subcategoryName === (subcategory.name_original ?? subcategory.name) && 'bg-white/20 font-semibold',
                      )}
                      onClick={() => handleSubcategoryClick(category, subcategory)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium flex items-center">
                            <span>{subcategory.name}</span>
                            {hasTreatments && (
                              <span
                                className={cn(
                                  'ml-2 text-xs px-1.5 py-0.5 rounded-full',
                                  showTreatmentsMenu
                                    ? 'bg-gray-200 text-gray-600'
                                    : 'bg-gray-100 text-gray-400',
                                )}
                              >
                                {subcategory.treatments.length}
                              </span>
                            )}
                          </div>
                        </div>
                        {shouldShowTreatments && (
                          <svg
                            className={cn(
                              'w-4 h-4 ml-2 transition-transform duration-200',
                              isSubcategoryExpanded ? 'rotate-90' : '',
                            )}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </button>

                    {/* Treatments */}
                    {shouldShowTreatments && isSubcategoryExpanded && (
                      <div className="bg-gray-50 border-t border-gray-200">
                        <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200 bg-gray-100">
                          {t('categories.treatments', { defaultValue: 'Behandlungen' })}
                        </div>
                        {subcategory.treatments.map((treatment, treatmentIndex) => {
                          // Deutscher Original-Key für den Filter-Store
                          const originalTreatment = subcategory.treatments_original?.[treatmentIndex] ?? treatment;
                          const isActive = treatmentName === originalTreatment;
                          return (
                          <button
                            type="button"
                            key={treatmentIndex}
                            className={cn(
                              'w-full px-6 py-2.5 text-start hover:bg-gray-100 transition-all duration-200 text-xs border-b border-gray-100 last:border-b-0',
                              isActive
                                ? 'bg-blue-100 text-blue-800 border-l-2 border-blue-500'
                                : 'text-gray-600 hover:text-gray-800',
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTreatmentClick(originalTreatment);
                            }}
                          >
                            <div className="flex items-center">
                              <div
                                className={cn(
                                  'w-1.5 h-1.5 rounded-full mr-3 flex-shrink-0',
                                  isActive ? 'bg-blue-500' : 'bg-gray-400',
                                )}
                              />
                              <span className="leading-relaxed">{treatment}</span>
                            </div>
                          </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>,
          document.body,
        )}
    </>
  );
}

/** Hilfsfunktion: subcategories-JSON sicher parsen */
function parseSubs(category: MedicalCategoryRow): Subcategory[] {
  if (!category.subcategories) return [];
  try {
    const parsed = category.subcategories as unknown as Subcategory[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
